import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  AVAILABLE_BRAND_FEATURES,
} from '@kouto/types';
import { useAppState } from 'AppProvider';
import { serializeParams } from 'utils';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import useIsMobile from 'hooks/useIsMobile';
import useSearchQueryParams from 'hooks/use-search-params';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { analyticsManager } from 'features/analytics';
import { CalendarAgendaItem, CalendarSession } from 'api/calendar/types';
import useScheduleViewConfig from 'hooks/useScheduleViewConfig';
import { TAB_SWAP_SESSION_STORAGE_KEY } from 'consts';
import { absoluteOffsetTop } from 'utils/absoluteOffsetTop';
import ScheduleView from './ScheduleView';
import {
  addDays,
  parseYmd,
  planDatePick,
  safeLocale,
  todayYmd,
  toYmd,
} from './scheduleViewUtils';

/** categoryIds from the URL: an array (embed state) or a CSV string (deep link). */
const toCategoryIds = (value: unknown): string[] => {
  if (Array.isArray(value)) return value as string[];
  if (typeof value === 'string' && value) return value.split(',');
  return [];
};

/** Stable string signature for a URL param that may be a string or array — used as a useMemo
 *  dep so identical-value-but-referentially-new URL params don't invalidate downstream memos. */
const toParamKey = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'string') return value;
  return '';
};

// We load one day at a time and append the next day as the user scrolls. Cap the run so an empty
// brand can't fan out unbounded requests; ~2 months of forward days is plenty for a session.
const MAX_LOADED_DAYS = 60;

// A picked day stays anchored until the user interacts (or picks again) — sections above it
// reflow long after they report "loaded": no-session days collapse from skeleton height,
// session days grow as thumbnails/fonts land, so any timed settle window still drifted the
// view a day off (WAY-15922). The anchor loop is a no-op position check per frame once
// aligned; the cap only bounds it if the user walks away mid-load.
const ANCHOR_MAX_MS = 30000;

// Land the section a hair PAST the scroll-spy line rather than exactly on it: offsetTop-based
// targeting and getBoundingClientRect disagree by a few pixels (collapsed margins), and a
// section resting marginally below the line makes the spy label the PREVIOUS day (WAY-15922's
// off-by-one). The sticky day heading absorbs the overlap invisibly.
const SPY_ALIGN_NUDGE_PX = 8;

const KOUTO_ROOT_ID = 'kouto-embed-root';
const SCHEDULE_NAV_SELECTOR = '.schedule-view__nav';

// The day-strip dots feature is intentionally not wired (it fanned out one request per day); the
// calendar is fetched per visible day instead.
const EMPTY_INVENTORY: Record<string, boolean> = {};

interface RestoreState {
  readonly selectedDate: string;
  readonly loadedDates: ReadonlyArray<string>;
  readonly scrollY: number;
}

/** sessionStorage key for back-restore, scoped to the route + active filters. */
const restoreKey = (search: Record<string, unknown>): string => {
  const categoryIds = toCategoryIds(search.categoryIds).join(',');
  const vibeIds = toCategoryIds(search.vibeIds).join(',');
  return `schedule-view:/calendar:${categoryIds}|${vibeIds}`;
};

const readRestore = (key: string): RestoreState | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RestoreState;
    if (!parsed?.selectedDate || !Array.isArray(parsed.loadedDates))
      return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeRestore = (key: string, state: RestoreState): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable (private mode / quota) — back-restore degrades gracefully.
  }
};

const ScheduleViewPage = () => {
  const history = useHistory();
  const { i18n, t: translate } = useTranslation();
  const { searchParams, replaceParams } = useSearchQueryParams();
  const config = useScheduleViewConfig();
  const isMobile = useIsMobile();
  // Pick the configured sticky offset for the current viewport. Desktop uses the original
  // `stickyTopOffset` key (unchanged DB shape); mobile uses the new per-viewport override.
  // Re-derives on breakpoint change because `useIsMobile` re-renders on resize.
  const stickyTopOffset = isMobile
    ? config.stickyTopOffsetMobile
    : config.stickyTopOffset;
  const brandName = useAppState(
    (state: { brand: { settings?: { name?: string } } }) =>
      state.brand.settings?.name,
  );
  const vibesV2Enabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.ENABLE_VIBES_V2,
  );

  // Validate the detected language before handing it to downstream Intl.DateTimeFormat calls.
  // i18next's 'path' detector can pick up any URL path segment as a language tag (e.g.
  // "experiences", "page"), and Intl throws a RangeError on invalid BCP-47 tags — which
  // otherwise crashes the schedule surface and trips the top-level ErrorBoundary.
  const locale = safeLocale(i18n.language || 'en-US');

  // Page-view analytics — the schedule view is the calendar surface, so it fires VIEW_CALENDAR
  // the same as the classic /calendar page. Waits on the brand settings so the payload can carry
  // the resolved brand name (matches the pattern used by the classic ExperiencesCalendar).
  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_CALENDAR,
    isNotReady: !brandName,
    payload: {
      [ANALYTICS_PROPERTY.PageName]: `${translate('experienceCalendar', {
        defaultValue: 'Experience Calendar',
      })} | ${brandName ?? ''}`,
      [ANALYTICS_PROPERTY.PageType]: 'Calendar Page',
    },
  });

  // The combined filter sets exactly one of category/vibe. If a hand-crafted/legacy URL carries
  // both, category wins (matching the dropdown's selectedValue) so the query never filters by both.
  // Memoised on stable string signatures so the derived array references stay stable across
  // renders when the URL params haven't changed — this keeps child memoisation (AgendaRow /
  // DaySection) effective when the scroll-spy rewrites unrelated URL params like `fromDate`.
  const categoryIdsKey = toParamKey(searchParams.categoryIds);
  const vibeIdsKey = toParamKey(searchParams.vibeIds);
  const categoryIds = useMemo(
    () => toCategoryIds(searchParams.categoryIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryIdsKey],
  );
  const vibeIds = useMemo(
    () => (categoryIds.length > 0 ? [] : toCategoryIds(searchParams.vibeIds)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryIds, vibeIdsKey],
  );
  // Stable signature of the effective filters (categoryIds win over vibeIds, matching the memos
  // above) — changes trigger a days refetch and reset append gating.
  const filterSig = `${categoryIds.join(',')}|${vibeIds.join(',')}`;

  const storageKey = restoreKey(searchParams);

  // Past dates are not valid entry points (no upcoming inventory exists before today). When the
  // URL's fromDate is in the past, ignore it entirely (state, restore, and URL all snap to today)
  // and rewrite the URL silently so the address bar matches what's rendered.
  const todayIso = todayYmd();
  const rawUrlDate = searchParams.fromDate as string | undefined;
  const urlDateIsPast = !!rawUrlDate && rawUrlDate < todayIso;
  const urlDate = urlDateIsPast ? undefined : rawUrlDate;

  // Restore (Back from a booking): the saved day + loaded days + scroll offset for this route.
  // Computed once on mount; only honoured when it matches the URL's fromDate (if any). Past-date
  // URLs also discard restore so the user lands on today rather than the previously-saved past day.
  const restoreRef = useRef<RestoreState | null>(null);
  if (restoreRef.current === null && !urlDateIsPast) {
    const saved = readRestore(storageKey);
    if (saved && (!urlDate || urlDate === saved.selectedDate)) {
      restoreRef.current = saved;
    }
  }
  const restore = restoreRef.current;

  // A Browse↔Schedule tab swap leaves an anchor (see ScheduleViewTabs); when present, the tab-bar
  // anchor owns scroll restoration on this mount, so we skip the sessionStorage scroll restore
  // below (they'd fight). The Back button leaves no anchor, so it still restores its offset. Peeked
  // once during render — before ScheduleViewTabs consumes the anchor in its layout effect.
  const isTabSwapRef = useRef<boolean | null>(null);
  if (isTabSwapRef.current === null) {
    try {
      isTabSwapRef.current =
        sessionStorage.getItem(TAB_SWAP_SESSION_STORAGE_KEY) != null;
    } catch {
      isTabSwapRef.current = false;
    }
  }

  const [selectedDate, setSelectedDate] = useState<string>(
    () => restore?.selectedDate || urlDate || todayIso,
  );

  // Sync the URL once when a past-date fromDate was rewritten — `replace`, no history entry, so
  // Back doesn't bounce the user to the invalid past date.
  useEffect(() => {
    if (urlDateIsPast) {
      replaceParams({ ...searchParams, fromDate: todayIso });
    }
    // Run only on mount; the rewrite is a one-shot redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The consecutive days currently rendered (oldest first). Reset to [selectedDate] on date change.
  const [loadedDates, setLoadedDates] = useState<string[]>(() =>
    restore && restore.loadedDates.length > 0
      ? [...restore.loadedDates]
      : [selectedDate],
  );

  // Which loaded days have finished their calendar request (so we only append the next once ready).
  const [loadedSet, setLoadedSet] = useState<Set<string>>(() => new Set());
  const [sentinelVisible, setSentinelVisible] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Set by a deliberate date selection (extend, reset, or a scroll fallback). The layout effect
  // below anchors that day's section under the sticky nav until the user takes over
  // (distinguishes a selection from scroll-spy / infinite-scroll updates to loadedDates).
  const pendingScrollToDateRef = useRef<string | null>(null);

  // Filters changed: every day refetches under a new URL, so clear which days count as "loaded"
  // (otherwise infinite scroll could append against stale load state). Mount run is a harmless no-op.
  const filterSigRef = useRef(filterSig);
  useEffect(() => {
    if (filterSigRef.current === filterSig) return;
    filterSigRef.current = filterSig;
    setLoadedSet(new Set());
    setSentinelVisible(false);
  }, [filterSig]);

  const handleDayLoaded = useCallback((date: string) => {
    setLoadedSet((prev) => {
      if (prev.has(date)) return prev;
      const next = new Set(prev);
      next.add(date);
      return next;
    });
  }, []);

  // Infinite scroll: observe the sentinel; append the next day when it nears the viewport AND the
  // current last day has resolved (keeps it to one request per day, no refetch of loaded days).
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setSentinelVisible(entry.isIntersecting),
      { rootMargin: '600px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!sentinelVisible) return;
    if (loadedDates.length >= MAX_LOADED_DAYS) return;
    const lastDate = loadedDates[loadedDates.length - 1];
    if (!loadedSet.has(lastDate)) return; // wait for the current tail day to finish first
    const nextDate = toYmd(addDays(parseYmd(lastDate), 1));
    setLoadedDates((prev) =>
      prev.includes(nextDate) ? prev : [...prev, nextDate],
    );
  }, [sentinelVisible, loadedDates, loadedSet]);

  // Latest-refs for values referenced by the stable callbacks below. Updating them during render
  // (rather than in a useEffect) keeps them in lockstep with the render's state, so a callback
  // fired from an event handler that resulted from THIS render's markup reads the current values.
  //
  // Refs — not the values themselves — are read inside the callbacks so `handleSelectDate` and
  // `handleBook` can be memoised with empty (or nearly-empty) dep arrays, giving them stable
  // identity across renders. That in turn lets AgendaRow / DaySection memoise effectively.
  const selectedDateRef = useRef(selectedDate);
  const loadedDatesRef = useRef(loadedDates);
  const searchParamsRef = useRef(searchParams);
  const storageKeyRef = useRef(storageKey);
  selectedDateRef.current = selectedDate;
  loadedDatesRef.current = loadedDates;
  searchParamsRef.current = searchParams;
  storageKeyRef.current = storageKey;

  // Bumped on every deliberate selection so the anchoring layout effect below re-runs even for
  // picks that change no other state (an already-loaded day: 'scroll' picks) — those clamped
  // one-shot near the tail of the window and drifted onto a neighbor like the others.
  const [anchorEpoch, setAnchorEpoch] = useState(0);

  // Day strip / anchor / picker selection. Every pick RESTARTS the window at the picked day —
  // it renders at the top with the following days streaming in below, which is what a pick
  // means ("show me this day onward"). Keeping/loading the in-between days ABOVE the pick, as
  // this used to, produced the WAY-15922 family: clicks stranded at capacity, landings drifting
  // onto the previous day as sections above reflowed, and "click the 24th, see the 23rd above".
  // Re-picking the day already at the top only re-anchors (no reload flash).
  const handleSelectDate = useCallback(
    (date: string) => {
      const { current } = loadedDatesRef;
      setSelectedDate(date);
      restoreRef.current = null; // a deliberate selection supersedes any restore snapshot
      replaceParams({ ...searchParamsRef.current, fromDate: date });

      pendingScrollToDateRef.current = date;
      setAnchorEpoch((epoch) => epoch + 1);

      if (planDatePick(date, current).kind === 'reset') {
        setLoadedDates([date]);
        setLoadedSet(new Set());
        setSentinelVisible(false);
      }
    },
    [replaceParams],
  );

  // Selection-triggered scroll anchoring, fired before paint so the user doesn't see the
  // browser's own scroll clamp during layout shift. A one-shot scroll isn't enough for any pick
  // that changes `loadedDates`: sections keep reflowing long after mounting (no-session days
  // collapse from skeleton height, session days grow as data/thumbnails land), which drifted the
  // view a day off the pick (WAY-15922) — and after a reset there isn't even enough scroll room
  // until the following days append. So the pick is ANCHORED: an rAF loop re-pins its section
  // under the sticky nav (a no-op position check per frame once aligned) until the user scrolls
  // (wheel/touch drag/keys), picks again, or ANCHOR_MAX_MS passes — the anchor never fights the
  // user.
  useLayoutEffect(() => {
    const root = document.getElementById(KOUTO_ROOT_ID);
    const nav = root?.querySelector<HTMLElement>(SCHEDULE_NAV_SELECTOR);
    if (!nav) return undefined;
    const offset = Math.max(0, stickyTopOffset ?? 0);
    const pendingDate = pendingScrollToDateRef.current;
    if (!pendingDate) return undefined;
    const section = root?.querySelector<HTMLElement>(
      `[data-schedule-day="${pendingDate}"]`,
    );
    if (!section) return undefined; // not mounted yet; effect re-runs on next loadedDates change

    const startedAt = performance.now();
    let frame = 0;
    const anchor = () => {
      frame = 0;
      if (pendingScrollToDateRef.current !== pendingDate) return;
      const target = root?.querySelector<HTMLElement>(
        `[data-schedule-day="${pendingDate}"]`,
      );
      if (!target) return;
      // Land the section top just below the sticky nav — matches the sticky-heading offset used
      // by the scroll-spy so a click on the strip mirrors "scrolled to this day".
      const top = Math.max(
        0,
        absoluteOffsetTop(target) -
          nav.offsetHeight -
          offset +
          SPY_ALIGN_NUDGE_PX,
      );
      if (Math.abs(window.scrollY - top) > 4) {
        window.scrollTo({ top, left: 0 });
      }
      if (performance.now() - startedAt < ANCHOR_MAX_MS) {
        frame = window.requestAnimationFrame(anchor);
      } else {
        pendingScrollToDateRef.current = null;
      }
    };
    // Scroll INTENT only — wheel, touch drag, keyboard. Plain taps/clicks (e.g. paging the day
    // strip while deciding) must not release the anchor, or the still-reflowing sections above
    // drift the view off the pick the user just made.
    const cancelAnchor = () => {
      pendingScrollToDateRef.current = null;
    };
    window.addEventListener('wheel', cancelAnchor, { passive: true });
    window.addEventListener('touchmove', cancelAnchor, { passive: true });
    window.addEventListener('keydown', cancelAnchor);
    anchor();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('wheel', cancelAnchor);
      window.removeEventListener('touchmove', cancelAnchor);
      window.removeEventListener('keydown', cancelAnchor);
    };
  }, [anchorEpoch, loadedDates, loadedSet, stickyTopOffset]);

  // Scroll-spy: as the user infinite-scrolls, highlight the day whose section sits under the sticky
  // nav and roll the day strip to it — without resetting the loaded list or scrolling. Updates the
  // URL fromDate (replace, no history entry) so Back from a booking still restores the right day.
  useEffect(() => {
    const root = document.getElementById(KOUTO_ROOT_ID);
    if (!root) return undefined;
    let frame = 0;
    const computeActive = () => {
      frame = 0;
      // A pick is being anchored: the user just declared the active day, and while its section
      // is still loading the scroll sits clamped short of it — spying here would relabel the
      // URL/strip to the neighboring day the pick is about to displace (the "falls back to the
      // previous day" flicker, WAY-15922). The spy resumes when the user takes over.
      if (pendingScrollToDateRef.current) return;
      const nav = root.querySelector('.schedule-view__nav');
      const line = nav ? nav.getBoundingClientRect().bottom : 0;
      // The active day is the lowest section whose top has reached the sticky nav.
      const active = Array.from(
        root.querySelectorAll<HTMLElement>('[data-schedule-day]'),
      ).reduce<string>(
        (acc, el) =>
          el.getBoundingClientRect().top <= line + 4
            ? el.dataset.scheduleDay ?? acc
            : acc,
        '',
      );
      if (active && active !== selectedDateRef.current) {
        setSelectedDate(active);
        replaceParams({ ...searchParamsRef.current, fromDate: active });
      }
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(computeActive);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    computeActive();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [replaceParams]);

  // Persist the current view (day + loaded days + scroll) so Back returns here. Saved on state
  // change and on scroll (rAF-throttled), and synchronously right before navigating to book.
  // Persist day + loaded-days changes WITHOUT clobbering the saved scroll offset. `window.scrollY`
  // is 0 at mount (the page starts at the top, before the restore scroll runs) and during route
  // transitions, so writing it here would wipe the offset the user actually left at — which is
  // exactly why Browse→Schedule kept landing at the top (saved scrollY read back as 0). The scroll
  // listener below owns `scrollY`; here we only carry forward whatever it last persisted.
  useEffect(() => {
    const existing = readRestore(storageKey);
    writeRestore(storageKey, {
      selectedDate,
      loadedDates,
      scrollY: existing?.scrollY ?? window.scrollY,
    });
  }, [storageKey, selectedDate, loadedDates]);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        writeRestore(storageKey, {
          selectedDate,
          loadedDates,
          scrollY: window.scrollY,
        });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [storageKey, selectedDate, loadedDates]);

  // Restore scroll offset on mount, retrying across frames until the loaded days have rendered tall
  // enough to reach the saved offset. This also wins over the global ScrollToTop reset on /calendar.
  useEffect(() => {
    // On a tab swap the ScheduleViewTabs anchor owns scroll — don't also restore here.
    if (isTabSwapRef.current) return undefined;
    if (!restore || restore.scrollY <= 0) return undefined;
    const target = restore.scrollY;
    const startedAt = Date.now();
    // Re-assert past the global ScrollToTop reset (it fires once ~50ms after the route change).
    const MIN_HOLD_MS = 250;
    const MAX_HOLD_MS = 1500;
    let frame = 0;
    const tryScroll = () => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({
        top: Math.min(target, Math.max(maxScroll, 0)),
        left: 0,
      });
      const elapsed = Date.now() - startedAt;
      const settled = Math.abs(window.scrollY - target) <= 2;
      // Keep nudging while content height grows (days resolve) AND until we've outlasted the reset.
      if ((!settled || elapsed < MIN_HOLD_MS) && elapsed < MAX_HOLD_MS) {
        frame = window.requestAnimationFrame(tryScroll);
      }
    };
    frame = window.requestAnimationFrame(tryScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
    };
    // Restore is a one-shot mount effect; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBook = useCallback(
    (item: CalendarAgendaItem, session: CalendarSession) => {
      // Snapshot synchronously so Back lands on this exact day + scroll position. Read from the
      // latest-refs (updated during render above) so the callback identity stays stable — closing
      // over `selectedDate` / `loadedDates` / `storageKey` directly would invalidate it (and
      // every memoised child that receives it) on every day-strip / scroll-spy update.
      writeRestore(storageKeyRef.current, {
        selectedDate: selectedDateRef.current,
        loadedDates: loadedDatesRef.current,
        scrollY: window.scrollY,
      });

      // Mirror the click_experience event fired by ListingItemCard on the landing pages so the
      // schedule view participates in the same downstream analytics funnel (GTM / GA4 / Adobe).
      analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
        [ANALYTICS_PROPERTY.ExperienceId]: item.subjectId,
        [ANALYTICS_PROPERTY.ExperienceTitle]: item.title ?? undefined,
        [ANALYTICS_PROPERTY.ExperienceCategory]: item.categoryName ?? undefined,
        [ANALYTICS_PROPERTY.Products]: JSON.stringify([
          {
            category: item.categoryName,
            id: item.subjectId,
            index: 0,
            itemListName: 'Calendar View',
            startingPrice: item.startingPrice,
            title: item.title,
          },
        ]),
      });

      const date = session.startDateTime.slice(0, 10);

      if (item.kind === 'resourceGroup') {
        // Prefer the collection's slug for a keyword-rich URL; fall back to the collection UUID.
        const collectionId =
          item.slug ?? item.resourceGroupCollectionId ?? item.subjectId;

        // Activate events (general_admission / reserved_seating) are modeled as a resource-group
        // collection but book through the event tickets flow — deep-link to the ticket selector.
        if (item.eventType != null) {
          history.push({ pathname: `/event/${collectionId}/tickets/${date}` });
          return;
        }

        // A decoupled group books the specific group (one row per Court #1/#2, etc.).
        const isDecoupledGroup =
          item.resourceGroupCollectionId != null &&
          item.resourceGroupCollectionId !== item.subjectId;
        if (isDecoupledGroup) {
          const time =
            session.startTime.length === 5
              ? `${session.startTime}:00`
              : session.startTime;
          history.push({
            pathname: `/collection/${collectionId}/group/${item.subjectId}`,
            search: serializeParams({
              date,
              time,
              duration: `PT${session.durationMinutes}M`,
            }),
          });
          return;
        }
        // Fallback: a non-decoupled resource collection — open the collection page.
        history.push({
          pathname: `/collection/${collectionId}`,
          search: serializeParams({ fromDate: date }),
        });
        return;
      }

      // Experiences deep-link to the clicked session's day (ExperienceView reads `sessionDate`).
      history.push({
        pathname: `/e/${item.slug ?? item.subjectId}`,
        search: serializeParams({
          previous: '/calendar',
          sessionDate: date,
          sessionTime: session.startTime,
        }),
      });
    },
    [history],
  );

  const showVibes = config.vibeFilter && vibesV2Enabled;

  // Memoised so the object identity is stable across renders — AgendaRow is React.memo'd and
  // relies on a stable `rowDisplay` prop reference to skip re-rendering.
  const rowDisplay = useMemo(
    () => ({
      image: config.rowImage,
      duration: config.rowDuration,
      spotsRemaining: config.rowSpotsRemaining,
    }),
    [config.rowImage, config.rowDuration, config.rowSpotsRemaining],
  );
  // Only `exactTime` grouping is supported today — one item per subject per day carrying all
  // sessions — so the collapse threshold works as intended and the client never has to render
  // period sub-headings. Turn this into a config-driven value again if we ship additional
  // groupings (`category`, `timeOfDay`, …) in the future.
  // const grouping = useMemo(() => config.groupBy, [config.groupBy]);

  return (
    <ScheduleView
      loadedDates={loadedDates}
      selectedDate={selectedDate}
      locale={locale}
      grouping="exactTime"
      categoryIds={categoryIds}
      vibeIds={vibeIds}
      inventory={EMPTY_INVENTORY}
      onSelectDate={handleSelectDate}
      onBook={handleBook}
      onDayLoaded={handleDayLoaded}
      sentinelRef={sentinelRef}
      collapseThreshold={config.chipsThreshold}
      categoryFilter={config.categoryFilter}
      vibeFilter={showVibes}
      rowDisplay={rowDisplay}
      stickyTopOffset={stickyTopOffset}
    />
  );
};

export default ScheduleViewPage;
